<template>
    <div class="activity-product-container">
        <!-- Activity list from statusID GSI -->
        <section class="activity-selector">
            <div class="section-heading">
                <div>
                    <h3>Activities for Status {{ statusID }}</h3>
                    <p>
                        Activities matching the given status ID.
                    </p>
                </div>

                <button
                    type="button"
                    class="secondary-button"
                    :disabled="loadingActivities"
                    @click="loadActivities"
                >
                    Refresh
                </button>
            </div>

            <p v-if="loadingActivities" class="status-message">
                Loading activities...
            </p>

            <p v-else-if="activityError" class="error-message">
                {{ activityError }}
            </p>

            <template v-else>
                <p class="result-count">
                    {{ activities.length }} activit{{
                        activities.length === 1 ? "y" : "ies"
                    }} found
                </p>

                <div
                    v-if="activities.length"
                    class="activity-list"
                >
                    <button
                        v-for="activity in activities"
                        :key="activity.code"
                        type="button"
                        class="activity-option"
                        :class="{
                            selected:
                                selectedActivity?.code === activity.code,
                        }"
                        @click="selectActivity(activity)"
                    >
                        <div class="activity-option-header">
                            <strong>
                                {{ activity.description || activity.code }}
                            </strong>

                            <span class="activity-code">
                                {{ activity.code }}
                            </span>
                        </div>

                        <div class="activity-details">
                            <span>
                                Status {{ activity.statusID }}
                            </span>

                            <span v-if="activity.productTemplate && activity.productTemplate.length">
                                {{ activity.productTemplate.length }}
                                template{{
                                    activity.productTemplate.length === 1
                                        ? ""
                                        : "s"
                                }}
                            </span>
                        </div>

                        <div
                            v-if="activity.productTemplate && activity.productTemplate.length"
                            class="template-tags"
                        >
                            <span
                                v-for="(
                                    template, index
                                ) in activity.productTemplate"
                                :key="`${activity.code}-${index}`"
                                class="template-tag"
                            >
                                {{ template.serviceCode || "No service" }}
                                <template v-if="template.productCode">
                                    / {{ template.productCode }}
                                </template>
                            </span>
                        </div>
                    </button>
                </div>

                <p v-else class="status-message">
                    No activities found for status {{ statusID }}.
                </p>
            </template>
        </section>

        <!-- Selected activity -->
        <section class="product-section">
            <div v-if="!selectedActivity" class="empty-selection">
                <h3>No Activity Selected</h3>

                <p>
                    Select an activity to view its products.
                </p>
            </div>

            <template v-else>
                <div class="selected-activity-header">
                    <div>
                        <span class="selected-label">
                            Selected Activity
                        </span>

                        <h2>
                            {{
                                selectedActivity.description ||
                                selectedActivity.code
                            }}
                        </h2>

                        <p>
                            Code: {{ selectedActivity.code }}

                            <template
                                v-if="
                                    selectedActivity.statusID !== null &&
                                    selectedActivity.statusID !== undefined
                                "
                            >
                                · Status: {{ selectedActivity.statusID }}
                            </template>
                        </p>
                    </div>

                    <button
                        type="button"
                        class="secondary-button"
                        @click="clearSelection"
                    >
                        Clear
                    </button>
                </div>

                <p v-if="loadingProducts" class="status-message">
                    Loading product information...
                </p>

                <p v-else-if="productError" class="error-message">
                    {{ productError }}
                </p>

                <div
                    v-else-if="loadedProducts.length"
                    class="product-list"
                >
                    <div
                        v-for="item in loadedProducts"
                        :key="item.id"
                        class="product-card"
                    >
                        <div class="product-header">
                            <div>
                                <h3>
                                    {{
                                        item.product?.description ||
                                        item.template.productCode
                                    }}
                                </h3>

                                <p>
                                    {{ item.template.serviceCode }}
                                    /
                                    {{ item.template.productCode }}
                                </p>
                            </div>

                            <span class="order-mode">
                                {{ item.template.orderMode || "Not set" }}
                            </span>
                        </div>

                        <div
                            v-if="item.notFound"
                            class="error-message"
                        >
                            <template v-if="item.notFoundMessage">
                                {{ item.notFoundMessage }}
                            </template>
                            <template v-else>
                                No Product was found for service code
                                <strong>
                                    {{ item.template.serviceCode }}
                                </strong>
                                and product code
                                <strong>
                                    {{ item.template.productCode }}
                                </strong>.
                            </template>
                        </div>

                        <div v-else class="attribute-list">
                            <div
                                v-for="attribute in item.attributes"
                                :key="attribute.name"
                                class="attribute-row"
                            >
                                <label
                                    :for="`${item.id}-${attribute.name}`"
                                >
                                    {{
                                        formatAttributeName(
                                            attribute.name,
                                            item
                                        )
                                    }}

                                    <span
                                        v-if="!attribute.editable"
                                        class="read-only-label"
                                    >
                                        Read only
                                    </span>
                                </label>

                                <!-- Boolean -->
                                <select
                                    v-if="
                                        attribute.editable &&
                                        getAttributeType(
                                            item,
                                            attribute.name
                                        ) === 'boolean'
                                    "
                                    :id="`${item.id}-${attribute.name}`"
                                    v-model="item.values[attribute.name]"
                                >
                                    <option :value="true">
                                        Yes
                                    </option>

                                    <option :value="false">
                                        No
                                    </option>
                                </select>

                                <!-- Number -->
                                <input
                                    v-else-if="
                                        attribute.editable &&
                                        getAttributeType(
                                            item,
                                            attribute.name
                                        ) === 'number'
                                    "
                                    :id="`${item.id}-${attribute.name}`"
                                    type="number"
                                    step="any"
                                    :value="item.values[attribute.name]"
                                    @input="
                                        updateAttributeValue(
                                            item,
                                            attribute.name,
                                            $event.target.value
                                        )
                                    "
                                />

                                <!-- Object or array -->
                                <textarea
                                    v-else-if="
                                        attribute.editable &&
                                        getAttributeType(
                                            item,
                                            attribute.name
                                        ) === 'json'
                                    "
                                    :id="`${item.id}-${attribute.name}`"
                                    :value="item.values[attribute.name]"
                                    rows="5"
                                    @input="
                                        updateAttributeValue(
                                            item,
                                            attribute.name,
                                            $event.target.value
                                        )
                                    "
                                />

                                <!-- String or unknown -->
                                <input
                                    v-else-if="attribute.editable"
                                    :id="`${item.id}-${attribute.name}`"
                                    type="text"
                                    :value="item.values[attribute.name]"
                                    @input="
                                        updateAttributeValue(
                                            item,
                                            attribute.name,
                                            $event.target.value
                                        )
                                    "
                                />

                                <!-- Read-only value -->
                                <div
                                    v-else
                                    class="read-only-value"
                                >
                                    {{
                                        displayValue(
                                            item.values[attribute.name]
                                        )
                                    }}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="hasValidProducts" class="apply-row">
                        <label v-if="canExcludeProduct" class="exclude-label">
                            <input type="checkbox" v-model="excludeProduct" />
                            Do not charge customer
                        </label>
                        <button
                            v-if="!hideApplyButton"
                            type="button"
                            class="apply-button"
                            @click="applyProductChanges"
                        >
                            Add Activity
                        </button>
                    </div>
                </div>

                <p v-else class="status-message">
                    This activity does not contain any product templates.
                </p>
            </template>
        </section>
    </div>
</template>

<script>
import { API } from "aws-amplify";

export default {
    name: "ActivityProductTemplate",

    props: {
        statusID: {
            type: Number,
            required: true,
        },
        hideApplyButton: {
            type: Boolean,
            default: false,
        },
        shipmentProducts: {
            type: Array,
            default: () => [],
        },
    },

    emits: ["activity-selected", "apply", "products-loaded"],

    data() {
        return {
            activities: [],
            selectedActivity: null,
            loadedProducts: [],

            loadingActivities: false,
            loadingProducts: false,

            activityError: "",
            productError: "",
            excludeProduct: false,
        };
    },

    computed: {
        hasValidProducts() {
            return this.loadedProducts.some(
                item => !item.notFound
            );
        },

        canExcludeProduct() {
            if (!this.selectedActivity || !this.selectedActivity.productTemplate) {
                return false;
            }
            return this.selectedActivity.productTemplate.some(
                t => t.canExclude === true
            );
        },
    },

    watch: {
        statusID: {
            immediate: false,
            handler() {
                this.clearSelection();
                this.loadActivities();
            },
        },
    },

    created() {
        this.loadActivities();
    },

    methods: {
        async loadActivities() {
            this.loadingActivities = true;
            this.activityError = "";

            const activitiesByStatusID = /* GraphQL */ `
                query ActivitiesByStatusID(
                    $statusID: Int!
                    $limit: Int
                    $nextToken: String
                ) {
                    activitiesByStatusID(
                        statusID: $statusID
                        limit: $limit
                        nextToken: $nextToken
                    ) {
                        items {
                            code
                            description
                            statusID
                            shipmentStage
                            shipmentPoint
                            shippingEvent
                            activityTime
                            recordedTime
                            comments
                            internalComments
                            notes
                            tripSheetId
                            latitude
                            longitude
                            driverCode
                            routeCode
                            vehicleCode
                            dropBoxCode
                            manifestSource
                            imageKeyID
                            imageKey
                            customerVisible
                            openManifestAction
                            notifyType
                            productTemplate {
                                serviceCode
                                productCode
                                orderMode
                                canExclude
                                attributes {
                                    name
                                    editable
                                }
                            }
                        }
                        nextToken
                    }
                }
            `;

            try {
                const activities = [];
                let nextToken = null;

                do {
                    const response = await API.graphql({
                        query: activitiesByStatusID,
                        variables: {
                            statusID: this.statusID,
                            limit: 1000,
                            nextToken,
                        },
                        authMode:
                            "AMAZON_COGNITO_USER_POOLS",
                    });

                    const result =
                        response.data?.activitiesByStatusID;

                    activities.push(
                        ...(result?.items || []).filter(Boolean)
                    );

                    nextToken = result?.nextToken || null;
                } while (nextToken);

                this.activities = activities.sort(
                    (first, second) => {
                        const firstName =
                            first.description ||
                            first.code ||
                            "";

                        const secondName =
                            second.description ||
                            second.code ||
                            "";

                        return firstName.localeCompare(
                            secondName
                        );
                    }
                );

                if (this.selectedActivity) {
                    const updatedSelection =
                        this.activities.find(activity => {
                            return (
                                activity.code ===
                                this.selectedActivity.code
                            );
                        });

                    if (updatedSelection) {
                        await this.selectActivity(
                            updatedSelection
                        );
                    } else {
                        this.clearSelection();
                    }
                } else if (this.activities.length) {
                    await this.selectActivity(
                        this.activities[0]
                    );
                }
            } catch (error) {
                console.error(
                    "Error loading activities:",
                    error
                );

                this.activityError =
                    "There was a problem loading the activities.";
            } finally {
                this.loadingActivities = false;
            }
        },

        async selectActivity(activity) {
            this.selectedActivity = activity;
            this.loadedProducts = [];
            this.productError = "";

            this.$emit("activity-selected", activity);

            await this.loadProductTemplates();
        },

        clearSelection() {
            this.selectedActivity = null;
            this.loadedProducts = [];
            this.productError = "";

            this.$emit("activity-selected", null);
        },

        async loadProductTemplates() {
            const templates =
                this.selectedActivity?.productTemplate || [];

            if (!templates.length) {
                return;
            }

            this.loadingProducts = true;
            this.productError = "";

            try {
                this.loadedProducts = await Promise.all(
                    templates.map((template, index) => {
                        return this.loadProduct(
                            template,
                            index
                        );
                    })
                );

                // Emit loaded products to the parent so they can be used
                // without requiring the user to click "Add Activity"
                const validProducts = this.loadedProducts
                    .filter(item => !item.notFound && item.product)
                    .map(item => {
                        const product = { ...item.product };
                        delete product.__typename;
                        return product;
                    });

                this.$emit("products-loaded", {
                    activity: this.selectedActivity,
                    products: validProducts,
                });
            } catch (error) {
                console.error(
                    "Error loading product templates:",
                    error
                );

                this.productError =
                    "There was a problem loading the product information.";
            } finally {
                this.loadingProducts = false;
            }
        },

        async loadCommonProduct(serviceCode) {
            // Fallback when the exact productCode + serviceCode product does not exist.
            // COMMON products are productCode-agnostic, so match on serviceCode within
            // the COMMON serviceNetwork.
            if (!serviceCode) return null;
            const query = /* GraphQL */ `
                query ProductByServiceNetwork($serviceNetwork: String!) {
                    productByServiceNetwork(serviceNetwork: $serviceNetwork) {
                        items {
                            productCode
                            serviceCode
                            description
                            fuelSurchargeCode
                            price
                            productName
                            serviceNetwork
                        }
                    }
                }
            `;
            try {
                const response = await API.graphql({
                    query,
                    variables: { serviceNetwork: "COMMON" },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });
                const items = response.data?.productByServiceNetwork?.items || [];
                return items.find(p => p.serviceCode === serviceCode) || null;
            } catch (error) {
                console.error("Error loading COMMON product:", error);
                return null;
            }
        },

        async loadProduct(template, index) {
            const getProductWithoutTimestamps = /* GraphQL */ `
                query GetProductWithoutTimestamps(
                    $productCode: String!
                    $serviceCode: String!
                ) {
                    getProduct(
                        productCode: $productCode
                        serviceCode: $serviceCode
                    ) {
                        productCode
                        serviceCode
                        
                        
                        
                        description
                        
                        fuelSurchargeCode
                        price
                        
                        productName
                        
                        
                        serviceNetwork
                        
                    }
                }
            `;

            const serviceCode = template?.serviceCode;
            let productCode = template?.productCode;

            // If shipmentProducts is provided, always derive productCode from the shipment's primary product
            if (this.shipmentProducts && this.shipmentProducts.length > 0) {
                const primaryProduct = this.shipmentProducts.find(
                    p => p.additionalService === false || p.additionalService === "false" || !p.additionalService
                );
                console.log("[ActivityProductTemplate] shipmentProducts:", JSON.stringify(this.shipmentProducts));
                console.log("[ActivityProductTemplate] primaryProduct found:", primaryProduct);
                if (primaryProduct && primaryProduct.productCode) {
                    productCode = primaryProduct.productCode;
                    console.log("[ActivityProductTemplate] Using derived productCode from shipment:", productCode);
                } else {
                    console.log("[ActivityProductTemplate] No primary product (additionalService=false) found on shipment");
                }
            } else {
                console.log("[ActivityProductTemplate] No shipmentProducts provided, using template productCode:", productCode);
            }

            console.log("[ActivityProductTemplate] Final lookup — productCode:", productCode, "serviceCode:", serviceCode);

            const id = `${
                productCode || "product"
            }-${serviceCode || "service"}-${index}`;

            const attributes = (
                template?.attributes || []
            ).filter(attribute => attribute?.name);

            if (!productCode || !serviceCode) {
                return {
                    id,
                    template: { ...template, productCode },
                    product: null,
                    attributes,
                    attributeTypes: {},
                    values: {},
                    notFound: true,
                    notFoundMessage: !productCode
                        ? "No primary product found on this shipment to derive the product code."
                        : "Service code is missing on the activity template.",
                };
            }

            try {
                const response = await API.graphql({
                    query: getProductWithoutTimestamps,
                    variables: {
                        productCode,
                        serviceCode,
                    },
                    authMode: "AMAZON_COGNITO_USER_POOLS",
                });

                let product =
                    response.data?.getProduct || null;

                // Fallback: if no exact product, use the COMMON serviceNetwork product
                // that matches this serviceCode (COMMON products are productCode-agnostic).
                if (!product) {
                    product = await this.loadCommonProduct(serviceCode);
                }

                if (!product) {
                    return {
                        id,
                        template: { ...template, productCode },
                        product: null,
                        attributes,
                        attributeTypes: {},
                        values: {},
                        notFound: true,
                        notFoundMessage: `Product does not exist for service code "${serviceCode}" and product code "${productCode}".`,
                    };
                }

                const values = {};
                const attributeTypes = {};

                for (const attribute of attributes) {
                    const attributeName = attribute.name;

                    const value =
                        Object.prototype.hasOwnProperty.call(
                            product,
                            attributeName
                        )
                            ? product[attributeName]
                            : null;

                    const type =
                        this.detectAttributeType(value);

                    attributeTypes[attributeName] = type;

                    values[attributeName] =
                        type === "json"
                            ? JSON.stringify(value, null, 2)
                            : value;
                }

                return {
                    id,
                    template: { ...template, productCode },
                    product,
                    attributes,
                    attributeTypes,
                    values,
                    notFound: false,
                };
            } catch (error) {
                console.error(
                    `Error loading product ${productCode}/${serviceCode}:`,
                    error
                );

                return {
                    id,
                    template: { ...template, productCode },
                    product: null,
                    attributes,
                    attributeTypes: {},
                    values: {},
                    notFound: true,
                };
            }
        },

        detectAttributeType(value) {
            if (typeof value === "boolean") {
                return "boolean";
            }

            if (typeof value === "number") {
                return "number";
            }

            if (
                Array.isArray(value) ||
                (
                    value !== null &&
                    typeof value === "object"
                )
            ) {
                return "json";
            }

            return "string";
        },

        getAttributeType(item, attributeName) {
            return (
                item.attributeTypes?.[attributeName] ||
                "string"
            );
        },

        updateAttributeValue(
            item,
            attributeName,
            inputValue
        ) {
            const type = this.getAttributeType(
                item,
                attributeName
            );

            if (type === "number") {
                item.values[attributeName] =
                    inputValue === ""
                        ? null
                        : Number(inputValue);

                return;
            }

            item.values[attributeName] = inputValue;
        },

        getFinalAttributeValue(item, attributeName) {
            const type = this.getAttributeType(
                item,
                attributeName
            );

            const value =
                item.values[attributeName];

            if (type === "json") {
                try {
                    return JSON.parse(value);
                } catch (error) {
                    throw new Error(
                        `${this.formatAttributeName(
                            attributeName
                        )} must contain valid JSON.`
                    );
                }
            }

            return value;
        },

        applyProductChanges() {
            if (!this.selectedActivity) {
                return;
            }

            this.productError = "";

            try {
                const products = this.loadedProducts
                    .filter(item => !item.notFound && item.product)
                    .map(item => {
                        /*
                        * Start with the complete Product returned by getProduct.
                        * This preserves fields such as productName, price,
                        * productFamily, vendor, and serviceNetwork.
                        */
                        const updatedProduct = {
                            ...item.product,
                            productCode: item.template.productCode,
                            serviceCode: item.template.serviceCode,
                        };

                        // Include orderMode from the template if available
                        if (item.template.orderMode) {
                            updatedProduct.orderMode = item.template.orderMode;
                        }

                        /*
                        * Overwrite Product fields with values changed in the UI.
                        * Read-only template attributes retain their original values.
                        */
                        for (const attribute of item.attributes || []) {
                            if (!attribute.editable) {
                                continue;
                            }

                            updatedProduct[attribute.name] =
                                this.getFinalAttributeValue(
                                    item,
                                    attribute.name
                                );
                        }

                        /*
                        * __typename is returned by some GraphQL queries but should
                        * not be saved as part of the Product object.
                        */
                        delete updatedProduct.__typename;

                        return updatedProduct;
                    });

                this.$emit("apply", {
                    activityCode: this.selectedActivity.code,
                    statusID: this.selectedActivity.statusID,
                    activity: this.selectedActivity,
                    products,
                    excludeProduct: this.excludeProduct,
                });
            } catch (error) {
                console.error(
                    "Error applying product template:",
                    error
                );

                this.productError =
                    error.message ||
                    "There was a problem applying the product template.";
            }
        },

        formatAttributeName(attributeName, item) {
            const isQuantity = attributeName && attributeName.toLowerCase() === "quantity";

            // When statusID is 77, show "Weight" instead of "Quantity"
            if (this.statusID === 77 && isQuantity) {
                return "Weight";
            }

            // Same day charges (statusID 78): label the quantity by its service code.
            if (this.statusID === 78 && isQuantity) {
                const serviceCode = (item && item.template && item.template.serviceCode) || "";
                const sameDayLabels = {
                    SDMILE: "Miles",
                    SDWAIT: "Wait Time",
                    SDTOLL: "Tolls"
                };
                if (sameDayLabels[serviceCode]) {
                    return sameDayLabels[serviceCode];
                }
            }

            return String(attributeName || "")
                .replace(/([A-Z])/g, " $1")
                .replace(/[_-]/g, " ")
                .replace(/\b\w/g, character => {
                    return character.toUpperCase();
                })
                .trim();
        },

        displayValue(value) {
            if (value === true) {
                return "Yes";
            }

            if (value === false) {
                return "No";
            }

            if (
                value === null ||
                value === undefined ||
                value === ""
            ) {
                return "Not provided";
            }

            return value;
        },
    },
};
</script>

<style scoped>
.activity-product-container {
    /* Scale the whole component down uniformly so every element (headers, labels,
       inputs, badges, spacing) shrinks together while keeping the relative size hierarchy. */
    zoom: 0.75;
    display: grid;
    grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
    gap: 20px;
    width: 100% - 10px;
    align-items: start;
    margin-top: 10px;
    padding-left: 5px;
    padding-right: 5px;
}

.activity-selector,
.product-section {
    padding: 18px;
    border: 1px solid #dfe3e8;
    border-radius: 10px;
    background-color: #ffffff;
    box-sizing: border-box;
}

.activity-selector {
    position: sticky;
    top: 15px;
}

.section-heading,
.selected-activity-header,
.product-header,
.activity-option-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
}

.section-heading h3,
.product-header h3 {
    margin: 0;
    font-size: 16px;
}

.selected-activity-header h2 {
    margin: 0;
    font-size: 19px;
}

.section-heading p,
.selected-activity-header p,
.product-header p {
    margin: 5px 0 0;
    color: #68717c;
    font-size: 13px;
}

.attribute-row input:focus,
.attribute-row select:focus,
.attribute-row textarea:focus {
    border-color: #1f5f99;
    outline: none;
    box-shadow: 0 0 0 2px rgba(31, 95, 153, 0.12);
}

.result-count {
    margin: 10px 0;
    color: #68717c;
    font-size: 12px;
}

.activity-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 570px;
    overflow-y: auto;
    padding-right: 3px;
}

.activity-option {
    width: 100%;
    padding: 12px;
    border: 1px solid #dfe3e8;
    border-radius: 7px;
    background-color: #ffffff;
    text-align: left;
    cursor: pointer;
}

.activity-option:hover {
    border-color: #8da9c4;
    background-color: #f8fafc;
}

.activity-option.selected {
    border-color: #1f5f99;
    background-color: #edf5fb;
}

.activity-code {
    flex-shrink: 0;
    padding: 3px 6px;
    border-radius: 4px;
    background-color: #eef1f4;
    color: #4d5660;
    font-size: 11px;
}

.activity-details {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 7px;
    color: #68717c;
    font-size: 12px;
}

.template-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 9px;
}

.template-tag {
    padding: 3px 7px;
    border-radius: 10px;
    background-color: #eef3f8;
    color: #38546e;
    font-size: 11px;
}

.selected-label {
    display: block;
    margin-bottom: 4px;
    color: #68717c;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

.product-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 18px;
}

.product-card {
    padding: 16px;
    border: 1px solid #dfe3e8;
    border-radius: 8px;
}

.product-header {
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 1px solid #eceff2;
}

.order-mode {
    flex-shrink: 0;
    padding: 4px 8px;
    border-radius: 4px;
    background-color: #eef3f8;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.attribute-list {
    display: flex;
    flex-direction: column;
    gap: 13px;
}

.attribute-row {
    display: grid;
    grid-template-columns: minmax(140px, 190px) 1fr;
    align-items: center;
    gap: 12px;
}

.attribute-row label {
    font-weight: 600;
}

.read-only-label {
    display: block;
    margin-top: 2px;
    color: #7a838d;
    font-size: 10px;
    font-weight: 400;
}

.attribute-row input,
.attribute-row select,
.attribute-row textarea {
    width: 100%;
    min-height: 40px;
    padding: 8px 10px;
    border: 1px solid #c7cdd4;
    border-radius: 6px;
    box-sizing: border-box;
    font: inherit;
}

.attribute-row textarea {
    resize: vertical;
    font-family: monospace;
}

.read-only-value {
    min-height: 40px;
    padding: 10px;
    border-radius: 6px;
    background-color: #f3f5f7;
    white-space: pre-wrap;
    word-break: break-word;
    box-sizing: border-box;
}

.secondary-button {
    padding: 6px 10px;
    border: 1px solid #c7cdd4;
    border-radius: 5px;
    background-color: #ffffff;
    cursor: pointer;
}

.secondary-button:hover {
    background-color: #f3f5f7;
}

.secondary-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.apply-button {
    padding: 10px 18px;
    border: none;
    border-radius: 6px;
    background-color: #33f18a;
    color: #ffffff;
    font-weight: 600;
    cursor: pointer;
}

.apply-button:hover {
    opacity: 0.9;
}

.apply-row {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 12px;
}

.exclude-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #555;
    cursor: pointer;
}

.status-message,
.empty-selection {
    padding: 25px 10px;
    color: #68717c;
    text-align: center;
}

.empty-selection h3 {
    margin: 0 0 7px;
    color: #303842;
    font-size: 15px;
}

.empty-selection p {
    margin: 0;
}

.error-message {
    padding: 10px;
    border-radius: 6px;
    background-color: #fff0f0;
    color: #9d2525;
}

@media (max-width: 800px) {
    /* Stack everything vertically on narrow/mobile screens. */
    .activity-product-container {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .activity-selector {
        position: static;
    }

    .activity-list {
        max-height: 330px;
    }

    /* Label above input instead of a cramped two-column row. */
    .attribute-row {
        grid-template-columns: 1fr;
        align-items: stretch;
        gap: 5px;
    }

    /* Headers stack their title/action vertically. */
    .section-heading,
    .selected-activity-header,
    .product-header,
    .activity-option-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }
}
</style>